import type { PolicyApiV2SearchResponse } from '~/components/PolicySearchV2';
import type POLICY_SEARCH_ENVELOPE_KINDS from '~/server_shared/generated-types/POLICY_SEARCH_ENVELOPE_KINDS';

/**
 * Enum representing the different states for FNOL recommendation display
 */
export enum FnolRecommendationState {
  /** AI is currently processing the email */
  PROCESSING = 'PROCESSING',
  /** AI processing completed with failure */
  PROCESSING_FAILED = 'PROCESSING_FAILED',
  /** No policy selected, user needs to select one */
  UNKNOWN_SUBORG_LOB_SELECT_POLICY = 'UNKNOWN_SUBORG_LOB_SELECT_POLICY',
  /** Policy search attempted but none found */
  POLICY_NOT_FOUND_BY_AI = 'POLICY_NOT_FOUND_BY_AI',
  /** Successful processing with recommendations available */
  RECOMMENDATION_EXISTS = 'RECOMMENDATION_EXISTS',
}

export interface FnolAiData {
  email_communication_id: string;
}

export type InboxType = 'in-queue' | 'done';

export interface PolicyData {
  policy_number: string;
  lob: string;
  effective_time?: string; // only exists on older format search results
  [key: string]: unknown;
}

export type Policy = {
  policy_number: string;
  policy_type: string;
  policy_data: PolicyData | PolicyApiV2SelectResult;
  retrieval_token?: string;
  /**
   * Which policy-search envelope this is. Optional because rows stored before the v1 marker
   * existed carry none — the same legacy case `_envelope_shape` interprets on the server.
   */
  response_payload_version_key?: (typeof POLICY_SEARCH_ENVELOPE_KINDS)[keyof typeof POLICY_SEARCH_ENVELOPE_KINDS];
};

export type PolicyApiV2SelectResult = {
  response_payload_version_key: (typeof POLICY_SEARCH_ENVELOPE_KINDS)['V2'];
  policy_search_response: PolicyApiV2SearchResponse;
  policy_api_data: unknown;
};

export interface RelatedClaimsStats {
  was_fuzzy_successful?: boolean;
  len_exact_found: number;
  len_after_fuzzy: number;
}

export interface FnolDraft {
  id: number;
  policy_id?: number;
  organization_id: number;
  sub_organization_name: string;
  draft: unknown;
  policy_lob: string;
  policy_number?: string;
  creating_adjuster: string;
  last_editing_adjuster: string;
  datetime_created: string;
  last_updated: string;
}

export type OnNewLossCallback = {
  fnolDraft?: FnolDraft;
  communicationId?: number;
  fnolAiRecommendationId: string | number;
  subOrgId: string | number | null;
  lob: string | null;
  policyData?: Policy;
  mixpanelExtra?: AiInboxMetadata | null;
  hasAiRecommendations?: boolean;
  isCliveEnabled?: boolean;
};

export interface AiInboxMetadata {
  fnol_inbox_item_id: number;
  has_related_claims: boolean;
  related_claims_count: number;
  organization_id: number;
  sub_organization_id: number;
  line_of_business: string;
  email_communication_id: number;
}

export interface PolicySearchValues {
  sub_org_id?: number | null;
  lob?: string | null;
  search?: string;
  search_criteria?: string;
  date_of_loss?: string;
  reported_date?: string;
}

export interface PolicyWithClaimsData {
  policy: Policy;
  claimValues: {
    date_of_loss?: string;
    reported_date?: string;
  };
}
